import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, Clipboard, Loader2, ShieldCheck } from 'lucide-react';
import { useApi, type EdgeInfo } from '../lib/api';
import {
  STAGE2_CONDUIT_CLIPBOARD_CLEANUP_MARGIN_MS,
  STAGE2_CONDUIT_PORTAL_ARM_WINDOW_MS,
  Stage2ConduitArmAttempt,
  confirmStage2ConduitArmAttempt,
  mintStage2ConduitPayloadWhileArmed,
  writeStage2ConduitBrokerPayloadOnce,
  type Stage2ConduitArmSnapshot,
} from '../lib/stage2ConduitArm';
import {
  STAGE2_CONTAINMENT_ACKNOWLEDGEMENT_WINDOW_SECONDS,
  Stage2ContainmentAcknowledgement,
  parseStage2SuccessorRecoveryProjection,
  stage2CommandSecondsRemaining,
  stage2ConduitBootstrapConfirmation,
  stage2SecretsReadyRecoveryRequest,
  stage2MigrationConfirmation,
  stage2LinuxResumeAvailable,
  stage2LinuxResumeConfirmation,
  stage2MacBootstrapCommandUsableUntil,
  stage2MigrationCommandUsableUntil,
  Stage2MigrationSession,
  type Stage2ContainmentAcknowledgementSnapshot,
  type Stage2MigrationMode,
  type Stage2MacSuccessorRecoveryRequest,
  type Stage2MacRecoveryRequest,
} from '../lib/stage2Migration';

interface Props {
  homeId: string;
  edge: EdgeInfo;
}

interface SafeMigrationResult {
  homeId: string;
  edgeId: string;
  commandUsableUntil: number;
  ttlSeconds: number;
  recoveryPhase: Stage2MacRecoveryRequest['phase'] | null;
  blindConduitRequired: boolean;
}

interface SafeBootstrapResult {
  homeId: string;
  edgeId: string;
  commandUsableUntil: number;
}

interface SafeCommandReceipt {
  homeId: string;
  edgeId: string;
  commandUsableUntil: number;
  recoveryType: string;
  copyKind: 'bootstrap' | 'broker-recovery' | 'migration';
}

type Stage2MigrationAction = 'migration' | 'darwin-recovery' | 'darwin-bootstrap';
type RecoveryMode = 'secrets-ready' | 'successor-projection' | null;

const STAGE2_CONDUIT_ARM_COMMAND =
  '/usr/bin/sudo -n -- /Library/PrivilegedHelperTools/com.manor.stage2-blind-conduit';
const STAGE2_CONDUIT_EXPIRED_ERROR =
  'The fixed root broker arm attempt expired. Run the trusted-console bootstrap again, generate a fresh projection, and begin a new arm attempt.';
const STAGE2_CONDUIT_NOT_READY_ERROR =
  'The root broker arm attempt is not begun, not acknowledged, or expired. Run a fresh trusted-console bootstrap if the attempt was consumed.';
const STAGE2_CONDUIT_LATE_CLIPBOARD_ERROR =
  'The broker payload clipboard write crossed the fixed arm cutoff and was cleared. Run the trusted-console bootstrap again before retrying.';
const STAGE2_CONDUIT_LATE_CLIPBOARD_CLEAR_FAILED_ERROR =
  'URGENT: Automatic clipboard clearing failed after the fixed arm cutoff. Never paste or run the broker payload. Immediately overwrite or clear the clipboard, then run the trusted-console bootstrap again. Do not retry this consumed attempt.';
const STAGE2_CONDUIT_DENIED_ERROR =
  'The root helper denied and consumed this arm attempt. Run the trusted-console bootstrap again, generate a fresh projection, and begin a new arm attempt.';

function formatCountdown(secondsRemaining: number): string {
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Super-admin operator bridge for the already fail-closed migration APIs.
 * The migration bearer command is deliberately never rendered, logged, or persisted:
 * it exists only in component memory until a dedicated click copies it, then it
 * is cleared whether the clipboard operation succeeds or fails.
 */
export function Stage2MigrationControl({ homeId, edge }: Props) {
  const api = useApi();
  const edgeId = edge.edgeId ?? '';
  const linuxResumeAvailable = stage2LinuxResumeAvailable(edge.securityPlatform);
  const migrationScope = `${homeId}\u0000${edgeId}\u0000${edge.securityPlatform ?? ''}`;
  const [migrationChoice, setMigrationChoice] = useState<{
    scope: string; mode: Stage2MigrationMode;
  }>(() => ({ scope: migrationScope, mode: 'initial' }));
  const migrationMode = migrationChoice.scope === migrationScope && linuxResumeAvailable
    ? migrationChoice.mode : 'initial';
  const migrationScopeFence = useRef(migrationScope);
  const controlId = useId();
  const recoveryHelpId = `${controlId}-recovery-help`;
  const recoveryErrorId = `${controlId}-recovery-error`;
  const secretsReadyHelpId = `${controlId}-secrets-ready-help`;
  const successorProjectionHelpId = `${controlId}-successor-projection-help`;
  const secretsReadyRadioId = `${controlId}-secrets-ready-radio`;
  const successorProjectionRadioId = `${controlId}-successor-projection-radio`;
  const secretsReadyInputId = `${controlId}-secrets-ready-input`;
  const successorProjectionInputId = `${controlId}-successor-projection-input`;
  const containmentConfirmationId = `${controlId}-containment-confirmation`;
  const session = useRef<Stage2MigrationSession | null>(null);
  const containmentAcknowledgement = useRef<Stage2ContainmentAcknowledgement | null>(null);
  const conduitArmAttempt = useRef<Stage2ConduitArmAttempt | null>(null);
  const conduitArmScope = useRef(`${homeId}\u0000${edgeId}`);
  if (!session.current && edgeId) session.current = new Stage2MigrationSession(homeId, edgeId);
  if (!containmentAcknowledgement.current && edgeId) {
    containmentAcknowledgement.current = new Stage2ContainmentAcknowledgement(homeId, edgeId);
  }
  if (!conduitArmAttempt.current) conduitArmAttempt.current = new Stage2ConduitArmAttempt();
  if (migrationScopeFence.current !== migrationScope) {
    // Platform changes are scope changes too. Invalidate before passive effects
    // so an old response cannot populate a newly classified edge's command.
    session.current?.invalidate();
    containmentAcknowledgement.current?.invalidate();
    conduitArmAttempt.current.invalidate();
    migrationScopeFence.current = migrationScope;
  }
  const nextConduitArmScope = `${homeId}\u0000${edgeId}`;
  if (conduitArmScope.current !== nextConduitArmScope) {
    // Match the session's synchronous prop fence: even before the passive
    // reset effect paints, old-home arm authority is unusable in this render.
    conduitArmAttempt.current.invalidate();
    conduitArmScope.current = nextConduitArmScope;
  }
  // Security-critical synchronous prop fence: an old promise cannot resolve in
  // the new home render and repopulate its command before a passive effect runs.
  if (session.current && edgeId) session.current.setScope(homeId, edgeId);
  else if (session.current) session.current.invalidate();
  if (containmentAcknowledgement.current && edgeId) {
    containmentAcknowledgement.current.setScope(homeId, edgeId);
    containmentAcknowledgement.current.setMode(migrationMode);
  } else {
    containmentAcknowledgement.current?.invalidate();
  }
  const [inFlight, setInFlight] = useState<Stage2MigrationAction | null>(null);
  const [migrationResult, setMigrationResult] = useState<SafeMigrationResult | null>(null);
  const [bootstrapResult, setBootstrapResult] = useState<SafeBootstrapResult | null>(null);
  const [recoveryMode, setRecoveryMode] = useState<RecoveryMode>(null);
  const [recoveryPriorTokenHash, setRecoveryPriorTokenHash] = useState('');
  const [recoveryProjection, setRecoveryProjection] = useState('');
  const [recoveryInputTouched, setRecoveryInputTouched] = useState(false);
  const [migrationCommandReady, setMigrationCommandReady] = useState(false);
  const [bootstrapCommandReady, setBootstrapCommandReady] = useState(false);
  const [commandReceipts, setCommandReceipts] = useState<readonly SafeCommandReceipt[]>([]);
  const [containmentReceipt, setContainmentReceipt] =
    useState<Stage2ContainmentAcknowledgementSnapshot | null>(null);
  const [nowEpochSeconds, setNowEpochSeconds] = useState(() => Math.floor(Date.now() / 1000));
  const [bootstrapCopiedForScope, setBootstrapCopiedForScope] = useState(false);
  const [conduitArmState, setConduitArmState] = useState<Stage2ConduitArmSnapshot>(
    () => conduitArmAttempt.current!.state(),
  );
  const [error, setError] = useState<string | null>(null);
  const commandReady = migrationCommandReady || bootstrapCommandReady;
  const containmentConfirmedForScope = edgeId !== ''
    && (containmentAcknowledgement.current?.isUsable(
      homeId,
      edgeId,
      Math.floor(Date.now() / 1000),
    ) ?? false);
  const visibleCommandReceipts = commandReceipts.filter(
    (receipt) => receipt.homeId === homeId
      && receipt.edgeId === edgeId
      && receipt.commandUsableUntil > nowEpochSeconds,
  );
  const hasActiveCountdown = commandReady
    || commandReceipts.length > 0
    || containmentConfirmedForScope;
  const conduitArmBegunForScope = conduitArmState.status === 'begun'
    || conduitArmState.status === 'acknowledged';
  const conduitArmConfirmedForScope = conduitArmState.status === 'acknowledged';

  const clearContainmentAcknowledgement = () => {
    containmentAcknowledgement.current?.invalidate();
    setContainmentReceipt(null);
  };

  useEffect(() => {
    // Never carry one home's authority into another route, even if React reuses
    // a component for the same physical edge's multi-home membership.
    session.current?.discardCommand();
    setInFlight(null);
    setMigrationCommandReady(false);
    setBootstrapCommandReady(false);
    setMigrationResult(null);
    setBootstrapResult(null);
    setCommandReceipts([]);
    setMigrationChoice({ scope: migrationScope, mode: 'initial' });
    clearContainmentAcknowledgement();
    setRecoveryMode(null);
    setRecoveryPriorTokenHash('');
    setRecoveryProjection('');
    setRecoveryInputTouched(false);
    setBootstrapCopiedForScope(false);
    setConduitArmState(conduitArmAttempt.current!.invalidate());
    setError(null);
  }, [homeId, edgeId, edge.securityPlatform]);

  useEffect(() => {
    if (!containmentReceipt) return undefined;
    const remainingMs = Math.max(0, (containmentReceipt.usableUntil * 1000) - Date.now());
    const timer = window.setTimeout(() => {
      clearContainmentAcknowledgement();
    }, remainingMs);
    return () => window.clearTimeout(timer);
  }, [containmentReceipt]);

  useEffect(() => {
    if (!hasActiveCountdown) return undefined;
    const tick = () => setNowEpochSeconds(Math.floor(Date.now() / 1000));
    tick();
    const timer = window.setInterval(tick, 1_000);
    return () => window.clearInterval(timer);
  }, [hasActiveCountdown]);

  useEffect(() => {
    if (commandReceipts.length === 0) return undefined;
    const earliestDeadline = Math.min(
      ...commandReceipts.map((receipt) => receipt.commandUsableUntil),
    );
    const remainingMs = Math.max(0, (earliestDeadline * 1000) - Date.now());
    const timer = window.setTimeout(() => {
      const now = Math.floor(Date.now() / 1000);
      setCommandReceipts((current) => current.filter(
        (receipt) => receipt.commandUsableUntil > now,
      ));
    }, remainingMs);
    return () => window.clearTimeout(timer);
  }, [commandReceipts]);

  useEffect(() => {
    return () => {
      // Invalidate both a held command and any response still in flight when
      // the operator leaves. A late response cannot re-arm the session.
      session.current?.invalidate();
      containmentAcknowledgement.current?.invalidate();
      conduitArmAttempt.current?.invalidate();
    };
  }, []);

  useEffect(() => {
    if (!migrationResult || !migrationCommandReady) return undefined;
    const remainingMs = Math.max(0, (migrationResult.commandUsableUntil * 1000) - Date.now());
    const timer = window.setTimeout(() => {
      session.current?.discardCommand();
      setMigrationCommandReady(false);
      setMigrationResult(null);
      clearContainmentAcknowledgement();
      setError('The one-time migration command expired and was discarded. Re-mint after rechecking authority.');
    }, remainingMs);
    return () => window.clearTimeout(timer);
  }, [migrationResult, migrationCommandReady]);

  useEffect(() => {
    if (!bootstrapResult || !bootstrapCommandReady) return undefined;
    const remainingMs = Math.max(0, (bootstrapResult.commandUsableUntil * 1000) - Date.now());
    const timer = window.setTimeout(() => {
      session.current?.discardCommand();
      setBootstrapCommandReady(false);
      setBootstrapResult(null);
      clearContainmentAcknowledgement();
      setError('The one-time Mac bootstrap expired and was discarded. Recheck the root projection and prepare it again.');
    }, remainingMs);
    return () => window.clearTimeout(timer);
  }, [bootstrapResult, bootstrapCommandReady]);

  useEffect(() => {
    if (conduitArmState.usableUntilMs === null) return undefined;
    const remainingMs = Math.max(0, conduitArmState.usableUntilMs - Date.now());
    const timer = window.setTimeout(() => {
      // The portal cutoff began before the root helper and leaves a cleanup
      // margin inside its authoritative 180-second capture deadline.
      setConduitArmState(conduitArmAttempt.current!.consume());
      session.current?.invalidate();
      setInFlight(null);
      setBootstrapCopiedForScope(false);
      setMigrationCommandReady(false);
      setMigrationResult(null);
      clearContainmentAcknowledgement();
      setError(STAGE2_CONDUIT_EXPIRED_ERROR);
    }, remainingMs);
    return () => window.clearTimeout(timer);
  }, [conduitArmState.usableUntilMs]);

  const successorRecovery = useMemo<Stage2MacSuccessorRecoveryRequest | null>(() => {
    if (recoveryMode !== 'successor-projection' || recoveryProjection.length === 0 || !edgeId) {
      return null;
    }
    try {
      return parseStage2SuccessorRecoveryProjection(recoveryProjection, homeId, edgeId);
    } catch {
      return null;
    }
  }, [edgeId, homeId, recoveryMode, recoveryProjection]);

  const secretsReadyHashValid = /^[0-9a-f]{64}$/.test(recoveryPriorTokenHash);
  const recoveryRequest: Stage2MacRecoveryRequest | null = recoveryMode === 'secrets-ready'
    ? secretsReadyHashValid
      ? stage2SecretsReadyRecoveryRequest(recoveryPriorTokenHash)
      : null
    : recoveryMode === 'successor-projection'
    ? successorRecovery
    : null;
  const recoveryInputFailsValidation = recoveryMode === 'secrets-ready'
    ? recoveryPriorTokenHash.length > 0 && !secretsReadyHashValid
    : recoveryMode === 'successor-projection'
    ? recoveryProjection.length > 0 && successorRecovery === null
    : false;
  const recoveryInputInvalid = recoveryInputTouched && recoveryInputFailsValidation;

  const selectRecoveryMode = (mode: Exclude<RecoveryMode, null>) => {
    // A mode switch discards both representations. The inactive checkpoint's
    // binding can never be silently retained and submitted from stale state.
    setRecoveryMode(mode);
    setRecoveryPriorTokenHash('');
    setRecoveryProjection('');
    setRecoveryInputTouched(false);
    clearContainmentAcknowledgement();
    setConduitArmState(conduitArmAttempt.current!.invalidate());
    setBootstrapCopiedForScope(false);
    setError(null);
  };

  const selectMigrationMode = (mode: Stage2MigrationMode) => {
    if (mode === 'linux-resume' && !linuxResumeAvailable) return;
    // Consume all old-mode state synchronously, including an in-flight ticket.
    session.current?.invalidate();
    clearContainmentAcknowledgement();
    containmentAcknowledgement.current?.setMode(mode);
    setConduitArmState(conduitArmAttempt.current!.invalidate());
    setMigrationChoice({ scope: migrationScope, mode });
    setInFlight(null);
    setMigrationCommandReady(false);
    setBootstrapCommandReady(false);
    setMigrationResult(null);
    setBootstrapResult(null);
    setCommandReceipts([]);
    setRecoveryMode(null);
    setRecoveryPriorTokenHash('');
    setRecoveryProjection('');
    setRecoveryInputTouched(false);
    setBootstrapCopiedForScope(false);
    setError(null);
  };

  const authorize = async (
    action: Exclude<Stage2MigrationAction, 'darwin-bootstrap'>,
    selectedRecovery?: Stage2MacRecoveryRequest,
  ) => {
    if (!edgeId || !session.current) return;
    if (commandReady) {
      setError('Copy or let the current one-time command expire before minting another authority.');
      return;
    }
    if (action === 'migration' && !(containmentAcknowledgement.current?.isUsable(
      homeId,
      edgeId,
      Math.floor(Date.now() / 1000),
    ) ?? false)) {
      clearContainmentAcknowledgement();
      setError(migrationMode === 'linux-resume'
        ? 'Confirm the exact expired-token Linux journal resume prerequisites and paired-home pin re-read before minting.'
        : 'Confirm the exact pre-mint host containment receipt and unanimous paired-home pin re-read before minting.');
      return;
    }
    const recovery = action === 'darwin-recovery';
    if (recovery && selectedRecovery === undefined) {
      setError('Select a recovery checkpoint and provide its exact valid root-journal binding.');
      return;
    }
    const blindConduitRecovery = selectedRecovery?.schemaVersion === 3;
    const confirmationMessage = action === 'migration' && migrationMode === 'linux-resume'
      ? stage2LinuxResumeConfirmation(homeId, edgeId, edge.securityPlatform)
      : stage2MigrationConfirmation(homeId, edgeId, selectedRecovery);
    if (recovery) clearContainmentAcknowledgement();
    if (blindConduitRecovery) {
      if (!bootstrapCopiedForScope) {
        setError(STAGE2_CONDUIT_NOT_READY_ERROR);
        return;
      }
      const confirmation = confirmStage2ConduitArmAttempt(
        conduitArmAttempt.current!,
        () => window.confirm(confirmationMessage),
      );
      setConduitArmState(conduitArmAttempt.current!.state());
      if (confirmation === 'cancelled') return;
      if (confirmation !== 'confirmed') {
        session.current.invalidate();
        setBootstrapCopiedForScope(false);
        setMigrationCommandReady(false);
        setMigrationResult(null);
        setError(STAGE2_CONDUIT_NOT_READY_ERROR);
        return;
      }
    } else {
      setConduitArmState(conduitArmAttempt.current!.invalidate());
      setBootstrapCopiedForScope(false);
      if (!window.confirm(confirmationMessage)) {
        clearContainmentAcknowledgement();
        return;
      }
    }
    if (action === 'migration') {
      const acknowledgementConsumed = containmentAcknowledgement.current?.consume(
        homeId,
        edgeId,
        Math.floor(Date.now() / 1000),
      ) ?? false;
      clearContainmentAcknowledgement();
      if (!acknowledgementConsumed) {
        setError('The containment acknowledgement expired before the migration request began. Verify a fresh exact receipt and paired-home pin re-read.');
        return;
      }
    }
    const request = session.current.begin(homeId, edgeId);
    if (!request) return;
    clearContainmentAcknowledgement();
    setInFlight(action);
    setError(null);
    // Clear operator-supplied recovery material synchronously, before the first
    // await. Only the narrow request stays in this invocation long enough to
    // cross the API boundary.
    setRecoveryPriorTokenHash('');
    setRecoveryProjection('');
    setRecoveryInputTouched(false);
    session.current.discardCommand();
    setMigrationCommandReady(false);
    setMigrationResult(null);
    setBootstrapCommandReady(false);
    setBootstrapResult(null);
    try {
      let result: Awaited<ReturnType<typeof api.mintStage2MigrationCommand>>;
      if (blindConduitRecovery) {
        const mint = await mintStage2ConduitPayloadWhileArmed(
          conduitArmAttempt.current!,
          () => api.mintStage2MigrationCommand(homeId, edgeId, selectedRecovery),
        );
        if (mint.status !== 'ready') {
          if (!session.current.isCurrent(request)) return;
          setConduitArmState(conduitArmAttempt.current!.state());
          setBootstrapCopiedForScope(false);
          session.current.invalidate();
          setInFlight(null);
          setError(STAGE2_CONDUIT_EXPIRED_ERROR);
          return;
        }
        result = mint.value;
      } else {
        result = await api.mintStage2MigrationCommand(homeId, edgeId, selectedRecovery);
      }
      if (!session.current.isCurrent(request)) return;
      const oneTimePayload = result.blindConduitRequired
        ? result.brokerRecoveryPayload
        : result.migrationInstallCommand;
      const commandUsableUntil = stage2MigrationCommandUsableUntil(result.expiresAt);
      if (!session.current.holdCommand(request, oneTimePayload, commandUsableUntil)) {
        if (session.current.isCurrent(request)) {
          setError('The one-time migration command expired before it could be prepared. Re-mint after rechecking authority.');
        }
        return;
      }
      setMigrationCommandReady(true);
      setMigrationResult({
        homeId: result.homeId,
        edgeId: result.edgeId,
        // The backend requires >120 seconds for controlled-migration claim
        // admission (and for the one allowed prior-token rotation). Discard
        // a not-yet-run command at that boundary; same-token retries are a
        // separate server-validated lifecycle.
        commandUsableUntil,
        ttlSeconds: result.ttlSeconds,
        recoveryPhase: selectedRecovery?.phase ?? null,
        blindConduitRequired: result.blindConduitRequired,
      });
      setNowEpochSeconds(Math.floor(Date.now() / 1000));
    } catch (caught) {
      if (session.current.isCurrent(request)) {
        setError(caught instanceof Error ? caught.message : 'Stage 2 migration request failed');
      }
    } finally {
      if (session.current.finish(request)) setInFlight(null);
    }
  };

  const prepareBootstrap = async () => {
    if (!edgeId || !session.current || !successorRecovery || recoveryProjection.length === 0) return;
    if (commandReady) {
      setError('Copy or let the current one-time command expire before preparing another authority.');
      return;
    }
    if (!window.confirm(stage2ConduitBootstrapConfirmation(homeId, edgeId, successorRecovery))) return;
    const projection = recoveryProjection;
    const request = session.current.begin(homeId, edgeId);
    if (!request) return;
    clearContainmentAcknowledgement();
    setInFlight('darwin-bootstrap');
    setError(null);
    session.current.discardCommand();
    setMigrationCommandReady(false);
    setMigrationResult(null);
    setBootstrapCommandReady(false);
    setBootstrapResult(null);
    setBootstrapCopiedForScope(false);
    setConduitArmState(conduitArmAttempt.current!.invalidate());
    // The complete projection exists only in this invocation across the API
    // boundary. Clear every rendered/input copy before the first await.
    setRecoveryPriorTokenHash('');
    setRecoveryProjection('');
    setRecoveryInputTouched(false);
    try {
      const result = await api.mintMacConduitBootstrap(homeId, edgeId, projection);
      const commandUsableUntil = stage2MacBootstrapCommandUsableUntil(result.expiresAt);
      if (!session.current.holdCommand(
        request,
        result.bootstrapInstallCommand,
        commandUsableUntil,
      )) {
        if (session.current.isCurrent(request)) {
          setError('The one-time Mac bootstrap expired before it could be prepared. Recheck the root projection and prepare it again.');
        }
        return;
      }
      setBootstrapCommandReady(true);
      setBootstrapResult({
        homeId: result.homeId,
        edgeId: result.edgeId,
        commandUsableUntil,
      });
      setNowEpochSeconds(Math.floor(Date.now() / 1000));
    } catch (caught) {
      if (session.current.isCurrent(request)) {
        setError(caught instanceof Error ? caught.message : 'Stage 2 bootstrap request failed');
      }
    } finally {
      if (session.current.finish(request)) setInFlight(null);
    }
  };

  const retainCommandReceipt = (receipt: SafeCommandReceipt) => {
    const now = Math.floor(Date.now() / 1000);
    if (receipt.homeId !== homeId
        || receipt.edgeId !== edgeId
        || receipt.commandUsableUntil <= now) {
      return;
    }
    setCommandReceipts((current) => [...current, receipt]);
    setNowEpochSeconds(now);
  };

  const copyAndForget = async (
    kind: 'bootstrap' | 'broker-recovery' | 'migration',
  ) => {
    clearContainmentAcknowledgement();
    if (kind === 'broker-recovery' && !conduitArmAttempt.current!.canUse()) {
      session.current?.invalidate();
      setConduitArmState(conduitArmAttempt.current!.state());
      setBootstrapCopiedForScope(false);
      setMigrationCommandReady(false);
      setMigrationResult(null);
      setError(STAGE2_CONDUIT_NOT_READY_ERROR);
      return;
    }
    // Consume synchronously before awaiting clipboard: a second click observes
    // null even while the first browser write is still pending. Re-check the
    // absolute deadline here as the timer may have been throttled during sleep.
    const consumed = session.current?.takeCommand(Math.floor(Date.now() / 1000)) ?? null;
    if (!consumed) {
      setMigrationCommandReady(false);
      setMigrationResult(null);
      setBootstrapCommandReady(false);
      setBootstrapResult(null);
      if (kind === 'bootstrap') setBootstrapCopiedForScope(false);
      if (kind === 'broker-recovery') {
        setConduitArmState(conduitArmAttempt.current!.consume());
        setBootstrapCopiedForScope(false);
      }
      setError(kind === 'bootstrap'
        ? 'The one-time Mac bootstrap expired and was discarded. Recheck the root projection and prepare it again.'
        : 'The one-time migration command expired and was discarded. Re-mint after rechecking authority.');
      return;
    }
    const { command, ticket } = consumed;
    const receipt = kind === 'bootstrap'
      ? bootstrapResult && {
        homeId: bootstrapResult.homeId,
        edgeId: bootstrapResult.edgeId,
        commandUsableUntil: bootstrapResult.commandUsableUntil,
        recoveryType: 'Mac trusted-console bootstrap',
        copyKind: kind,
      }
      : migrationResult && {
        homeId: migrationResult.homeId,
        edgeId: migrationResult.edgeId,
        commandUsableUntil: migrationResult.commandUsableUntil,
        recoveryType: migrationResult.recoveryPhase === null
          ? 'Root migration'
          : migrationResult.blindConduitRequired
          ? `Mac ${migrationResult.recoveryPhase} broker recovery`
          : `Mac ${migrationResult.recoveryPhase} recovery`,
        copyKind: kind,
      };
    setError(null);
    setMigrationCommandReady(false);
    setMigrationResult(null);
    setBootstrapCommandReady(false);
    setBootstrapResult(null);
    try {
      if (kind === 'broker-recovery') {
        const outcome = await writeStage2ConduitBrokerPayloadOnce(
          command,
          conduitArmAttempt.current!,
          navigator.clipboard.writeText.bind(navigator.clipboard),
          () => session.current?.isCurrent(ticket) ?? false,
        );
        setConduitArmState(conduitArmAttempt.current!.state());
        setBootstrapCopiedForScope(false);
        if (outcome !== 'copied') {
          session.current?.invalidate();
          clearContainmentAcknowledgement();
          setError(outcome === 'late-cleared'
            ? STAGE2_CONDUIT_LATE_CLIPBOARD_ERROR
            : outcome === 'late-clear-failed'
            ? STAGE2_CONDUIT_LATE_CLIPBOARD_CLEAR_FAILED_ERROR
            : STAGE2_CONDUIT_EXPIRED_ERROR);
          return;
        }
        if (receipt) {
          retainCommandReceipt(receipt);
        }
        return;
      }

      await navigator.clipboard.writeText(command);
      if (session.current?.isCurrent(ticket)) {
        if (receipt) {
          retainCommandReceipt(receipt);
        }
        if (kind === 'bootstrap') {
          setBootstrapCopiedForScope(true);
          setConduitArmState(conduitArmAttempt.current!.resetForFreshBootstrap());
        }
      }
    } catch {
      if (session.current?.isCurrent(ticket)) {
        clearContainmentAcknowledgement();
        if (kind === 'bootstrap') {
          setBootstrapCopiedForScope(false);
          setConduitArmState(conduitArmAttempt.current!.invalidate());
        }
        if (kind === 'broker-recovery') {
          setBootstrapCopiedForScope(false);
          setConduitArmState(conduitArmAttempt.current!.consume());
        }
        setError('Clipboard copy failed. The command was discarded; re-mint after checking clipboard access.');
      }
    }
  };

  const beginConduitArmAttempt = () => {
    clearContainmentAcknowledgement();
    if (!bootstrapCopiedForScope || successorRecovery === null) {
      setConduitArmState(conduitArmAttempt.current!.invalidate());
      setBootstrapCopiedForScope(false);
      setError('Run the trusted-console bootstrap and paste its fresh root projection before beginning an arm attempt.');
      return;
    }
    const begun = conduitArmAttempt.current!.begin();
    if (begun === null) {
      const current = conduitArmAttempt.current!.state();
      setConduitArmState(current);
      if (current.status === 'consumed' || current.status === 'unavailable') {
        setBootstrapCopiedForScope(false);
      }
      setError('This bootstrap already has an arm attempt or is no longer available. Do not restart its deadline; run a fresh bootstrap if the attempt was consumed.');
      return;
    }
    setConduitArmState(begun);
    setError(null);
  };

  const confirmConduitArmed = () => {
    clearContainmentAcknowledgement();
    if (!bootstrapCopiedForScope || successorRecovery === null) {
      setConduitArmState(conduitArmAttempt.current!.invalidate());
      setBootstrapCopiedForScope(false);
      setError('Run the trusted-console bootstrap and paste its fresh root projection before acknowledging the broker.');
      return;
    }
    const acknowledged = conduitArmAttempt.current!.acknowledgeArmed();
    if (acknowledged === null) {
      const current = conduitArmAttempt.current!.state();
      setConduitArmState(current);
      if (current.status === 'acknowledged') {
        setError('MANOR_STAGE2_CONDUIT_ARMED was already acknowledged. The original cutoff is unchanged.');
      } else {
        setBootstrapCopiedForScope(false);
        setError(STAGE2_CONDUIT_EXPIRED_ERROR);
      }
      return;
    }
    setConduitArmState(acknowledged);
    setError(null);
  };

  const denyConduitArmAttempt = () => {
    clearContainmentAcknowledgement();
    setConduitArmState(conduitArmAttempt.current!.denied());
    session.current?.invalidate();
    setInFlight(null);
    setBootstrapCopiedForScope(false);
    setMigrationCommandReady(false);
    setMigrationResult(null);
    setError(STAGE2_CONDUIT_DENIED_ERROR);
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-200/60">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-medium text-brand-ink inline-flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-slate-500" aria-hidden="true" /> Stage 2 controlled migration
          </div>
          <p className="text-[11px] text-slate-600 mt-1 max-w-2xl">
            The backend re-checks the exact home, full edge ID, paired-home set, canary assignment,
            frozen edge platform, and 300-second window. This mints the ordinary root migration
            for an already-enrolled Linux or Mac edge. It is not the first pre-protocol Linux
            canary authorization.
          </p>
          <dl className="mt-1 text-[11px] text-slate-500">
            <div className="grid gap-0.5 sm:grid-cols-[4.5rem_1fr]">
              <dt className="font-sans font-medium">Edge ID</dt>
              <dd className="break-all font-mono">{edgeId || 'Unavailable'}</dd>
            </div>
          </dl>
        </div>
        <div className="w-full max-w-2xl rounded border border-warn-attention/30 bg-warn-attention/5 p-2 text-[11px] leading-4 text-ink-800">
          {linuxResumeAvailable && (
            <fieldset className="mb-2 border-b border-warn-attention/20 pb-2"
              disabled={!edgeId || inFlight !== null || conduitArmBegunForScope}>
              <legend className="mb-1 font-medium">Linux migration checkpoint</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <label className="inline-flex items-center gap-1.5">
                  <input type="radio" name={`${controlId}-migration-mode`}
                    checked={migrationMode === 'initial'}
                    onChange={() => selectMigrationMode('initial')}
                    className="h-3.5 w-3.5 accent-warn-attention" />
                  Initial migration — no installer journal
                </label>
                <label className="inline-flex items-center gap-1.5">
                  <input type="radio" name={`${controlId}-migration-mode`}
                    checked={migrationMode === 'linux-resume'}
                    onChange={() => selectMigrationMode('linux-resume')}
                    className="h-3.5 w-3.5 accent-warn-attention" />
                  Resume after prior token expiry
                </label>
              </div>
              <p className="mt-1 text-slate-600">
                Changing checkpoints discards the command and all acknowledgements.
                Resume keeps the existing journal and exact target.
              </p>
            </fieldset>
          )}
          <label htmlFor={containmentConfirmationId} className="flex items-start gap-2">
            <input
              id={containmentConfirmationId}
              type="checkbox"
              checked={containmentConfirmedForScope}
              onChange={(event) => {
                if (event.target.checked && edgeId && containmentAcknowledgement.current) {
                  const now = Math.floor(Date.now() / 1000);
                  try {
                    setContainmentReceipt(
                      containmentAcknowledgement.current.acknowledge(homeId, edgeId, now),
                    );
                    setNowEpochSeconds(now);
                  } catch {
                    clearContainmentAcknowledgement();
                    setError('The containment acknowledgement could not be started. Recheck the system clock and exact edge scope.');
                    return;
                  }
                } else {
                  clearContainmentAcknowledgement();
                }
                setError(null);
              }}
              disabled={!edgeId || inFlight !== null || commandReady || conduitArmBegunForScope}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-warn-attention"
            />
            <span>
              {migrationMode === 'linux-resume' ? <>
                I verified the exact root-owned schema-4 Linux journal is at contained,
                secrets-ready, identity-ready, registration-bound, registered, or manifest-verified,
                for the same expected target SHA and canary. The durable root hold and containment
                receipt remain intact; the old updater job and process/cgroup are stopped;
                updater locks, pending deploy, publish state, and the enrollment lock are absent.
                Current, deployed, and manifest identities still match the frozen source, and
                every paired-home pin was strongly re-read as that same exact canary.
                The journal’s actual prior tokenExpiresAt has passed — not just the earlier
                command admission cutoff. I will preserve the journal and hold.
                This exact-scope acknowledgement is single-use and valid for
                {' '}{STAGE2_CONTAINMENT_ACKNOWLEDGEMENT_WINDOW_SECONDS / 60} minutes.
              </> : <>
              I verified the exact non-secret pre-mint containment receipt: the durable root hold
              is present; the old updater job, process/cgroup, locks, pending deploy, publish state,
              and installer journal are absent; current, deployed, and manifest identities still
              match the frozen source; then every paired-home pin was set and strongly re-read as
              the same exact canary. This exact-scope acknowledgement is single-use and valid for
              {' '}{STAGE2_CONTAINMENT_ACKNOWLEDGEMENT_WINDOW_SECONDS / 60} minutes.
              </>}
            </span>
          </label>
          {containmentConfirmedForScope
            && containmentReceipt?.homeId === homeId
            && containmentReceipt.edgeId === edgeId && (
            <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-warn-attention/20 pt-2 text-slate-600">
              <span className="font-medium text-ink-800">Exact-scope acknowledgement active</span>
              <span>
                Usable for{' '}
                <span
                  role="timer"
                  className="font-mono font-semibold text-ink-800"
                  aria-label={`${stage2CommandSecondsRemaining(containmentReceipt.usableUntil, nowEpochSeconds)} seconds remaining`}
                >
                  {formatCountdown(stage2CommandSecondsRemaining(
                    containmentReceipt.usableUntil,
                    nowEpochSeconds,
                  ))}
                </span>
                {' '}· until {new Date(containmentReceipt.usableUntil * 1000).toLocaleTimeString()}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => authorize('migration')}
            disabled={!edgeId || !containmentConfirmedForScope || inFlight !== null || commandReady
              || conduitArmBegunForScope}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-slate-300 bg-white text-xs text-ink-800 hover:bg-slate-100 disabled:opacity-40"
          >
            {inFlight === 'migration'
              ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
              : <ShieldCheck size={12} aria-hidden="true" />}
            Mint one-time root migration command
          </button>
        </div>
      </div>

      <fieldset
        disabled={!edgeId || inFlight !== null || commandReady}
        className="mt-3 rounded-md border border-slate-200 bg-white/60 p-3 disabled:opacity-60"
      >
        <legend className="px-1 text-[11px] font-semibold text-brand-ink">
          Mac recovery checkpoint
        </legend>
        <p id={recoveryHelpId} className="text-[11px] leading-4 text-slate-600">
          Mac only. Select the exact root-journal checkpoint. Nothing is selected by default,
          and changing checkpoints clears the prior input. Do not use this for Linux root migration.
        </p>

        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-xs text-ink-800">
          <label htmlFor={secretsReadyRadioId} className="inline-flex items-center gap-2">
            <input
              id={secretsReadyRadioId}
              name={`${controlId}-recovery-mode`}
              type="radio"
              checked={recoveryMode === 'secrets-ready'}
              onClick={clearContainmentAcknowledgement}
              onChange={() => selectRecoveryMode('secrets-ready')}
              aria-describedby={recoveryHelpId}
              className="h-3.5 w-3.5 accent-warn-attention"
            />
            Secrets-ready hash
          </label>
          <label htmlFor={successorProjectionRadioId} className="inline-flex items-center gap-2">
            <input
              id={successorProjectionRadioId}
              name={`${controlId}-recovery-mode`}
              type="radio"
              checked={recoveryMode === 'successor-projection'}
              onClick={clearContainmentAcknowledgement}
              onChange={() => selectRecoveryMode('successor-projection')}
              aria-describedby={recoveryHelpId}
              className="h-3.5 w-3.5 accent-warn-attention"
            />
            In-progress projection
          </label>
        </div>

        {recoveryMode === 'secrets-ready' && (
          <label htmlFor={secretsReadyInputId} className="mt-3 block text-[11px] text-slate-600">
            Root-journal prior token SHA-256
            <span id={secretsReadyHelpId} className="mt-0.5 block leading-4 text-slate-500">
              Paste only the lowercase SHA-256 binding produced by the root recovery tool—never
              the one-time token itself.
            </span>
            <input
              id={secretsReadyInputId}
              type="text"
              value={recoveryPriorTokenHash}
              onChange={(event) => setRecoveryPriorTokenHash(event.target.value)}
              onBlur={() => setRecoveryInputTouched(true)}
              maxLength={64}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="64 lowercase hex characters"
              aria-invalid={recoveryInputInvalid}
              aria-describedby={`${recoveryHelpId} ${secretsReadyHelpId}${recoveryInputInvalid ? ` ${recoveryErrorId}` : ''}`}
              className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 font-mono text-xs text-ink-800"
            />
          </label>
        )}

        {recoveryMode === 'successor-projection' && (
          <label htmlFor={successorProjectionInputId} className="mt-3 block text-[11px] text-slate-600">
            Complete secret-free root projection JSON
            <span id={successorProjectionHelpId} className="mt-0.5 block leading-4 text-slate-500">
              Paste the complete secret-free JSON projection produced by the root recovery tool—not
              the root journal, a token, or a key.
            </span>
            <textarea
              id={successorProjectionInputId}
              value={recoveryProjection}
              onChange={(event) => {
                setRecoveryProjection(event.target.value);
                // Any mutation after Begin consumes the attempt. A changed
                // projection can never inherit or recreate its fixed cutoff.
                if (conduitArmBegunForScope) {
                  clearContainmentAcknowledgement();
                  setConduitArmState(conduitArmAttempt.current!.projectionMutated());
                  setBootstrapCopiedForScope(false);
                  setError('The projection changed after the arm attempt began. Run the trusted-console bootstrap again before retrying.');
                }
              }}
              onBlur={() => setRecoveryInputTouched(true)}
              rows={7}
              maxLength={16 * 1024}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="Paste the complete root projection JSON"
              aria-invalid={recoveryInputInvalid}
              aria-describedby={`${recoveryHelpId} ${successorProjectionHelpId}${recoveryInputInvalid ? ` ${recoveryErrorId}` : ''}`}
              className="mt-1 w-full resize-y rounded border border-slate-300 bg-white px-2 py-2 font-mono text-[11px] leading-4 text-ink-800"
            />
          </label>
        )}

        {recoveryInputInvalid && (
          <div id={recoveryErrorId} role="alert" className="mt-2 text-[11px] text-alert-breach">
            {recoveryMode === 'successor-projection'
              ? 'Projection is invalid or does not match this home and edge.'
              : 'Enter exactly 64 lowercase hexadecimal characters.'}
          </div>
        )}

        {successorRecovery && (
          <div
            role="status"
            className="mt-2 rounded border border-armed-ok/30 bg-armed-ok/5 p-2 text-[11px] text-ink-800"
          >
            <div className="font-semibold text-ink-800">
              Root projection parsed · {successorRecovery.phase} · ready for server verification
            </div>
            <dl className="mt-1 grid gap-1 font-mono">
              <div className="grid gap-0.5 sm:grid-cols-[4.5rem_1fr]">
                <dt className="font-sans text-slate-500">SHA</dt>
                <dd className="break-all">{successorRecovery.priorExpectedReleaseSha}</dd>
              </div>
              <div className="grid gap-0.5 sm:grid-cols-[4.5rem_1fr]">
                <dt className="font-sans text-slate-500">Channel</dt>
                <dd className="break-all">{successorRecovery.priorExpectedReleaseChannel}</dd>
              </div>
            </dl>
          </div>
        )}
        {successorRecovery && !bootstrapCopiedForScope && (
          <p className="mt-2 text-[11px] leading-4 text-slate-600">
            Prepare and run the trusted-console bootstrap first. This page will then require a
            fresh root projection before it enables the broker-only recovery payload.
          </p>
        )}
        {successorRecovery && bootstrapCopiedForScope && (
          <div className="mt-2 rounded border border-warn-attention/30 bg-warn-attention/5 p-2 text-[11px] leading-4 text-ink-800">
            <div className="font-semibold">Arm the one-shot root broker after this fresh projection</div>
            <p className="mt-1 text-slate-600">
              The bootstrap installs and authenticates the helper; it does not arm it. Select
              Begin immediately before running the command. Begin records one immutable
              {' '}{STAGE2_CONDUIT_PORTAL_ARM_WINDOW_MS / 1000}-second portal cutoff before the
              root helper starts, preserving a
              {' '}{STAGE2_CONDUIT_CLIPBOARD_CLEANUP_MARGIN_MS / 1000}-second clipboard-cleanup
              margin inside the helper's 180-second limit.
            </p>
            {!conduitArmBegunForScope ? (
              <button
                type="button"
                onClick={beginConduitArmAttempt}
                className="mt-2 inline-flex items-center gap-1.5 rounded border border-warn-attention/40 bg-white px-2.5 py-1.5 text-xs text-ink-800 hover:bg-warn-attention/10"
              >
                <ShieldCheck size={12} aria-hidden="true" />
                Begin arm attempt
              </button>
            ) : (
              <>
                <div role="status" className="mt-1 text-slate-600">
                  Fixed portal cutoff:
                  {' '}{new Date(conduitArmState.usableUntilMs!).toLocaleTimeString()}. A later
                  acknowledgement cannot move it.
                </div>
                <p className="mt-1 text-slate-600">
                  Now run this exact no-argument command in the same trusted local Terminal:
                </p>
                <code className="mt-1 block break-all rounded border border-slate-200 bg-white px-2 py-1.5 text-[11px] text-ink-800">
                  {STAGE2_CONDUIT_ARM_COMMAND}
                </code>
                <p className="mt-1 text-slate-600">
                  Acknowledge only after Terminal prints
                  {' '}<code>MANOR_STAGE2_CONDUIT_ARMED</code>, then mint and copy immediately. If it
                  prints <code>MANOR_STAGE2_CONDUIT_DENIED</code>, consume this attempt below; a new
                  trusted-console bootstrap and fresh projection are required.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={confirmConduitArmed}
                    disabled={conduitArmConfirmedForScope}
                    aria-pressed={conduitArmConfirmedForScope}
                    className="inline-flex items-center gap-1.5 rounded border border-warn-attention/40 bg-white px-2.5 py-1.5 text-xs text-ink-800 hover:bg-warn-attention/10 disabled:opacity-60"
                  >
                    {conduitArmConfirmedForScope
                      ? <Check size={12} className="text-armed-ok" aria-hidden="true" />
                      : <ShieldCheck size={12} aria-hidden="true" />}
                    I observed MANOR_STAGE2_CONDUIT_ARMED
                  </button>
                  <button
                    type="button"
                    onClick={denyConduitArmAttempt}
                    className="inline-flex items-center gap-1.5 rounded border border-alert-breach/30 bg-white px-2.5 py-1.5 text-xs text-alert-breach hover:bg-alert-breach/5"
                  >
                    MANOR_STAGE2_CONDUIT_DENIED
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={prepareBootstrap}
            disabled={!edgeId || inFlight !== null || successorRecovery === null || commandReady
              || conduitArmBegunForScope}
            className="inline-flex items-center gap-1.5 rounded border border-warn-attention/40 bg-warn-attention/5 px-2.5 py-1.5 text-xs text-ink-800 hover:bg-warn-attention/10 disabled:opacity-40"
          >
            {inFlight === 'darwin-bootstrap'
              ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
              : <ShieldCheck size={12} aria-hidden="true" />}
            Prepare one-time Mac trusted-console bootstrap
          </button>
          <button
            type="button"
            onClick={() => recoveryRequest && authorize('darwin-recovery', recoveryRequest)}
            disabled={!edgeId || inFlight !== null || recoveryRequest === null || commandReady
              || (recoveryRequest?.schemaVersion === 3
                && (!bootstrapCopiedForScope || !conduitArmConfirmedForScope))}
            className="inline-flex items-center gap-1.5 rounded border border-warn-attention/40 bg-warn-attention/5 px-2.5 py-1.5 text-xs text-ink-800 hover:bg-warn-attention/10 disabled:opacity-40"
          >
            {inFlight === 'darwin-recovery'
              ? <Loader2 size={12} className="animate-spin" aria-hidden="true" />
              : <Clipboard size={12} aria-hidden="true" />}
            {recoveryRequest?.schemaVersion === 3
              ? 'Mint one-time Mac broker recovery payload'
              : 'Mint one-time Mac recovery command'}
          </button>
        </div>
      </fieldset>

      {migrationResult && migrationCommandReady && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded border border-warn-attention/30 bg-warn-attention/5 p-2 text-xs text-ink-800">
          <div className="min-w-0 flex-1">
            <div role="status">
              One-time {migrationResult.recoveryPhase
                ? `${migrationResult.recoveryPhase} Mac recovery `
                : 'root migration '}
              {migrationResult.blindConduitRequired ? 'broker payload' : 'command'} ready for{' '}
              {migrationResult.homeId} /{' '}
              <span className="break-all font-mono">{migrationResult.edgeId}</span>.
              {migrationResult.blindConduitRequired
                ? ' It is not displayed or persisted. Copy it only while the root broker is armed; never paste or run it in Terminal.'
                : ' It is not displayed or persisted. After running it, overwrite the clipboard with harmless text.'}
            </div>
            <div className="mt-1 text-[11px] text-slate-600">
              Usable for{' '}
              <span
                role="timer"
                className="font-mono font-semibold text-ink-800"
                aria-label={`${stage2CommandSecondsRemaining(migrationResult.commandUsableUntil, nowEpochSeconds)} seconds remaining`}
              >
                {formatCountdown(stage2CommandSecondsRemaining(
                  migrationResult.commandUsableUntil,
                  nowEpochSeconds,
                ))}
              </span>
              {' '}· until {new Date(migrationResult.commandUsableUntil * 1000).toLocaleTimeString()}
            </div>
          </div>
          <button
            type="button"
            onClick={() => copyAndForget(
              migrationResult.blindConduitRequired ? 'broker-recovery' : 'migration',
            )}
            disabled={migrationResult.blindConduitRequired && !conduitArmConfirmedForScope}
            className="inline-flex items-center gap-1.5 rounded border border-warn-attention/40 bg-white px-2.5 py-1.5 hover:bg-warn-attention/10 disabled:opacity-40"
          >
            <Clipboard size={12} aria-hidden="true" />
            {migrationResult.blindConduitRequired
              ? 'Copy for armed broker once and clear'
              : 'Copy command once'}
          </button>
        </div>
      )}
      {bootstrapResult && bootstrapCommandReady && (
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3 rounded border border-warn-attention/30 bg-warn-attention/5 p-2 text-xs text-ink-800">
          <div className="min-w-0 flex-1">
            <div role="status">
              Trusted-console bootstrap command ready for {bootstrapResult.homeId} /{' '}
              <span className="break-all font-mono">{bootstrapResult.edgeId}</span>.
              It is not displayed or persisted. Run it once in a trusted local Terminal session,
              then overwrite the clipboard with harmless text. After sudo starts, the root stager
              authenticates the exact publisher artifacts but does not arm the broker. Observe
              MANOR_STAGE2_CONDUIT_BOOTSTRAP_READY, then generate a fresh root projection and
              follow the separate no-argument arm step.
            </div>
            <div className="mt-1 text-[11px] text-slate-600">
              Usable for{' '}
              <span
                role="timer"
                className="font-mono font-semibold text-ink-800"
                aria-label={`${stage2CommandSecondsRemaining(bootstrapResult.commandUsableUntil, nowEpochSeconds)} seconds remaining`}
              >
                {formatCountdown(stage2CommandSecondsRemaining(
                  bootstrapResult.commandUsableUntil,
                  nowEpochSeconds,
                ))}
              </span>
              {' '}· until {new Date(bootstrapResult.commandUsableUntil * 1000).toLocaleTimeString()}
            </div>
          </div>
          <button
            type="button"
            onClick={() => copyAndForget('bootstrap')}
            className="inline-flex items-center gap-1.5 rounded border border-warn-attention/40 bg-white px-2.5 py-1.5 hover:bg-warn-attention/10"
          >
            <Clipboard size={12} aria-hidden="true" /> Copy command once
          </button>
        </div>
      )}

      {visibleCommandReceipts.map((receipt, index) => {
        const secondsRemaining = stage2CommandSecondsRemaining(
          receipt.commandUsableUntil,
          nowEpochSeconds,
        );
        return (
          <section
            key={`${receipt.homeId}\u0000${receipt.edgeId}\u0000${receipt.commandUsableUntil}\u0000${index}`}
            aria-label="Copied one-time authority receipt"
            className="mt-2 rounded border border-armed-ok/30 bg-armed-ok/5 p-2 text-xs text-ink-800"
          >
            <div role="status" className="flex items-start gap-1.5 font-semibold">
              <Check size={12} className="mt-0.5 shrink-0 text-armed-ok" aria-hidden="true" />
              <span>
                One-time authority copied; bearer removed from portal memory.
                {receipt.copyKind === 'broker-recovery'
                  ? ' Let the already-armed root broker consume and clear it; do not paste or run it.'
                  : receipt.copyKind === 'bootstrap'
                  ? ' Run it in the trusted Terminal, then overwrite the clipboard with harmless text.'
                  : ' Run it while this receipt is active, then overwrite the clipboard with harmless text.'}
              </span>
            </div>
            <dl className="mt-1 grid gap-1 text-[11px]">
              <div className="grid gap-0.5 sm:grid-cols-[5.5rem_1fr]">
                <dt className="text-slate-500">Type</dt>
                <dd>{receipt.recoveryType}</dd>
              </div>
              <div className="grid gap-0.5 sm:grid-cols-[5.5rem_1fr]">
                <dt className="text-slate-500">Home</dt>
                <dd className="break-all font-mono">{receipt.homeId}</dd>
              </div>
              <div className="grid gap-0.5 sm:grid-cols-[5.5rem_1fr]">
                <dt className="text-slate-500">Edge ID</dt>
                <dd className="break-all font-mono">{receipt.edgeId}</dd>
              </div>
              <div className="grid gap-0.5 sm:grid-cols-[5.5rem_1fr]">
                <dt className="text-slate-500">Usable for</dt>
                <dd>
                  <span
                    role="timer"
                    className="font-mono font-semibold"
                    aria-label={`${secondsRemaining} seconds remaining`}
                  >
                    {formatCountdown(secondsRemaining)}
                  </span>
                  {' '}· until {new Date(receipt.commandUsableUntil * 1000).toLocaleTimeString()}
                </dd>
              </div>
            </dl>
          </section>
        );
      })}
      {error && (
        <div role="alert" className="mt-2 rounded border border-alert-breach/30 bg-alert-breach/5 p-2 text-xs text-alert-breach">
          {error}
        </div>
      )}
    </div>
  );
}
